import { h } from '@/components/dom';
import { button, checkbox, note, numberField, section, select, slider } from '@/components/controls';
import type { App } from './App';
import type { PinShape, Point } from '@/types/engine';

type ShapeId = PinShape['type'];

export function createPinsPanel(app: App): HTMLElement {
  const pins = () => app.state.config.pins;
  const shape = select<ShapeId>({
    label: 'Shape',
    value: pins().shape.type,
    options: [
      { value: 'circle', label: 'Circle' },
      { value: 'rectangle', label: 'Rectangle' },
      { value: 'polygon', label: 'Polygon (custom outline)' },
      { value: 'custom', label: 'Custom pin positions' },
    ],
    onChange: (v) => app.updateConfig((c) => (c.pins.shape = shapeFor(v, c.pins.shape))),
  });
  const count = slider({ label: 'Pin count', min: 3, max: 2000, step: 1, value: pins().count, onInput: () => {}, onChange: (v) => app.updateConfig((c) => (c.pins.count = Math.round(v))) });
  const padding = slider({ label: 'Padding', min: 0, max: 200, step: 1, value: pins().padding, format: (v) => `${v} px`, onInput: () => {}, onChange: (v) => app.updateConfig((c) => (c.pins.padding = v)) });
  const rotation = slider({ label: 'Rotation', min: -180, max: 180, step: 1, value: pins().rotation, format: (v) => `${v}°`, onInput: () => {}, onChange: (v) => app.updateConfig((c) => (c.pins.rotation = v)) });
  const scale = slider({ label: 'Scale', min: 0.2, max: 1.5, step: 0.01, value: pins().scale, onInput: () => {}, onChange: (v) => app.updateConfig((c) => (c.pins.scale = v)) });
  const minDistance = slider({ label: 'Minimum pin distance', min: 0, max: 100, step: 0.5, value: pins().minDistance, format: (v) => `${v} px`, onInput: () => {}, onChange: (v) => app.updateConfig((c) => (c.pins.minDistance = v)) });
  const distribution = select<'uniform' | 'jittered'>({
    label: 'Distribution',
    value: pins().distribution.type,
    options: [
      { value: 'uniform', label: 'Uniform' },
      { value: 'jittered', label: 'Jittered (seeded)' },
    ],
    onChange: (v) => app.updateConfig((c) => (c.pins.distribution = v === 'uniform' ? { type: 'uniform' } : { type: 'jittered', amount: 0.5 })),
  });
  const initialDistribution = pins().distribution;
  const jitter = slider({ label: 'Jitter amount', min: 0, max: 0.95, step: 0.05, value: initialDistribution.type === 'jittered' ? initialDistribution.amount : 0.5, onInput: () => {}, onChange: (v) => app.updateConfig((c) => (c.pins.distribution = { type: 'jittered', amount: v })) });
  const seed = numberField({ label: 'Seed', value: app.state.config.seed, min: 0, step: 1, onChange: (v) => app.updateConfig((c) => (c.seed = Math.max(0, Math.floor(v)))) });
  const showPins = checkbox({ label: 'Show pins', value: app.state.ui.showPins, onChange: (v) => app.updateUi({ showPins: v }) });
  const showIds = checkbox({ label: 'Show pin numbers', value: app.state.ui.showPinIds, onChange: (v) => app.updateUi({ showPinIds: v }) });

  const pointsArea = h('textarea', { class: 'ctl-textarea', rows: 5, placeholder: '[[0,0],[1,0],[1,1],[0,1]]  (normalized 0..1 coordinates)' });
  const applyPoints = button({
    label: 'Apply points',
    onClick: () => {
      try {
        const parsed = JSON.parse(pointsArea.value) as unknown;
        if (!Array.isArray(parsed) || parsed.some((p) => !Array.isArray(p) || p.length < 2 || typeof p[0] !== 'number' || typeof p[1] !== 'number')) {
          throw new Error('expected an array of [x, y] pairs');
        }
        const points = parsed.map((p) => [p[0], p[1]] as Point);
        app.updateConfig((c) => {
          c.pins.shape = c.pins.shape.type === 'custom' ? { type: 'custom', points } : { type: 'polygon', points };
        });
        app.notify(`Applied ${points.length} points.`, 'success');
      } catch (err) {
        app.fail(`Invalid points: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
  });
  const pointsSection = section('Outline / positions', [note('For polygon and custom shapes: JSON array of [x, y] in normalized frame coordinates (0..1).'), pointsArea, applyPoints]);
  const infoLine = h('div', { class: 'info-box' });

  app.store.subscribe((s, p) => {
    if (s.config !== p.config || s === p) {
      const q = s.config.pins;
      shape.setValue(q.shape.type);
      count.setValue(q.count);
      count.setDisabled(q.shape.type === 'custom');
      padding.setValue(q.padding);
      rotation.setValue(q.rotation);
      scale.setValue(q.scale);
      minDistance.setValue(q.minDistance);
      distribution.setValue(q.distribution.type);
      jitter.setDisabled(q.distribution.type !== 'jittered');
      if (q.distribution.type === 'jittered') jitter.setValue(q.distribution.amount);
      seed.setValue(s.config.seed);
      const hasPoints = q.shape.type === 'polygon' || q.shape.type === 'custom';
      pointsSection.hidden = !hasPoints;
      if (hasPoints && document.activeElement !== pointsArea) {
        const pts = (q.shape as { points: Point[] }).points;
        pointsArea.value = pts.length > 64 ? `${JSON.stringify(pts.slice(0, 64).map((v) => [round(v[0]), round(v[1])]))} … (${pts.length} points)` : JSON.stringify(pts.map((v) => [round(v[0]), round(v[1])]));
      }
    }
    if (s.ui !== p.ui || s === p) {
      showPins.setValue(s.ui.showPins);
      showIds.setValue(s.ui.showPinIds);
    }
    if (s.solution.pins !== p.solution.pins || s === p) infoLine.textContent = `${s.solution.pins.length >>> 1} pins generated`;
  }, { immediate: true });

  return h(
    'div',
    null,
    section('Layout', [shape.element, count.element, padding.element, rotation.element, scale.element, infoLine]),
    section('Spacing', [minDistance.element, distribution.element, jitter.element, seed.element]),
    pointsSection,
    section('Display', [showPins.element, showIds.element]),
  );
}

function round(v: number): number {
  return Math.round(v * 10000) / 10000;
}

function shapeFor(id: ShapeId, previous: PinShape): PinShape {
  switch (id) {
    case 'circle':
      return { type: 'circle' };
    case 'rectangle':
      return { type: 'rectangle' };
    case 'polygon':
      return { type: 'polygon', points: 'points' in previous && previous.points.length >= 3 ? previous.points : [[0.5, 0], [1, 0.38], [0.81, 1], [0.19, 1], [0, 0.38]] };
    case 'custom':
      return { type: 'custom', points: 'points' in previous && previous.points.length >= 2 ? previous.points : defaultCustomPoints(36) };
  }
}

function defaultCustomPoints(n: number): Point[] {
  const pts: Point[] = [];
  for (let i = 0; i < n; i++) {
    const a = -Math.PI / 2 + (i / n) * Math.PI * 2;
    const r = 0.5 * (i % 2 === 0 ? 1 : 0.85);
    pts.push([0.5 + r * Math.cos(a), 0.5 + r * Math.sin(a)]);
  }
  return pts;
}
